'use client';

import { c as _c } from "react/compiler-runtime";
import { ChartNoDataEmptyState } from '@/comps/charts/analytics-chart/ChartNoDataEmptyState';
import { Button } from '@/comps/primitives/button';
import { CHART_EMPTY_ERROR_DESCRIPTION, CHART_EMPTY_ERROR_TITLE } from '@/constants/misc.constants';
import { UISelect } from '@/comps/ui/UISelect';
import { CompositionLegend } from '@/domains/pages/ai-spend/components/composition-legend';
import { SpendCompositionBar } from '@/domains/pages/ai-spend/components/spend-composition-bar';
import { SpendDistributionTable } from '@/domains/pages/ai-spend/components/spend-distribution-table';
import { SPEND_DISTRIBUTION_CARD_LOADING_BODY_HEIGHT_PX, SPEND_DISTRIBUTION_CARD_TITLE, SPEND_DISTRIBUTION_CARD_TOOLTIP, SPEND_GROUP_EMPTY_TITLE, SPEND_GROUP_OPTIONS, type SpendGroupKind } from '@/domains/pages/ai-spend/constants';
import type { GroupSpendRow } from '@/domains/pages/ai-spend/utils/map-ai-spend-distribution';
import type { CompositionSegment } from '@/domains/pages/ai-spend/utils/map-ai-spend-aggregates';
import { NebulyPageCard } from '@/domains/pages/shared/nebuly-page-card';
import { PAGE_DATA_NUMBER_CLASS } from '@/domains/pages/shared/typography';
import { formatPercentage, formatSpend } from '@/utils/format.util';
import { cn } from '@/utils/shad';
import { useMemo, useState } from 'react';
type SpendDistributionCardProps = {
  kind: SpendGroupKind;
  onKindChange: (kind: SpendGroupKind) => void;
  options?: {
    value: SpendGroupKind;
    label: string;
  }[];
  rows: GroupSpendRow[];
  compositionSegments: CompositionSegment[];
  isLoading?: boolean;
  isError?: boolean;
  onRetry?: () => void;
  className?: string;
};
export function SpendDistributionCard(t0) {
  const $ = _c(22);
  const {
    kind,
    onKindChange,
    options: t1,
    rows,
    compositionSegments,
    isLoading: t2,
    isError: t3,
    onRetry,
    className
  } = t0;
  const options = t1 === undefined ? SPEND_GROUP_OPTIONS : t1;
  const isLoading = t2 === undefined ? false : t2;
  const isError = t3 === undefined ? false : t3;
  const [hoveredKey, setHoveredKey] = useState(null);
  let t4;
  if ($[0] !== compositionSegments) {
    t4 = compositionSegments.map(_temp);
    $[0] = compositionSegments;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const legendItems = t4;
  const hasRows = rows.length > 0;
  let t5;
  if ($[2] !== className) {
    t5 = cn("min-h-0", className);
    $[2] = className;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  let t6;
  if ($[4] !== kind || $[5] !== onKindChange || $[6] !== options) {
    t6 = <UISelect value={kind} options={options} onChange={onKindChange} ariaLabel="Group AI spend by" className="w-fit" />;
    $[4] = kind;
    $[5] = onKindChange;
    $[6] = options;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== compositionSegments || $[9] !== hasRows || $[10] !== hoveredKey || $[11] !== isError || $[12] !== isLoading || $[13] !== kind || $[14] !== legendItems || $[15] !== onRetry || $[16] !== rows) {
    t7 = isError ? <div className="flex flex-col items-start" data-testid="spend-distribution-error-state"><ChartNoDataEmptyState title={CHART_EMPTY_ERROR_TITLE} description={CHART_EMPTY_ERROR_DESCRIPTION} className="py-8" />{onRetry ? <Button variant="defaultDark" size="sm" className="mb-8 ml-8 h-8 rounded-full px-4 text-xs" onClick={onRetry}>Try again</Button> : null}</div> : isLoading ? <div className="w-full" style={{
      height: SPEND_DISTRIBUTION_CARD_LOADING_BODY_HEIGHT_PX
    }} /> : hasRows ? <><div className="flex flex-col gap-3"><SpendCompositionBar segments={compositionSegments} hoveredKey={hoveredKey} onHoverChange={setHoveredKey} /><CompositionLegend items={legendItems} hoveredKey={hoveredKey} onHoverChange={setHoveredKey} formatValue={item => kind === "task" && item.share != null ? <span className={PAGE_DATA_NUMBER_CLASS}>{formatPercentage(item.share, {
            inputScale: "decimal",
            decimals: 1
          })}</span> : <>{formatSpend(item.value)}{item.share != null ? <>{" "}<span className={PAGE_DATA_NUMBER_CLASS}>({formatPercentage(item.share, {
                inputScale: "decimal",
                decimals: 1
              })})</span></> : null}</>} /></div><SpendDistributionTable rows={rows} kind={kind} /></> : <div data-testid="spend-distribution-empty-state"><ChartNoDataEmptyState title={SPEND_GROUP_EMPTY_TITLE[kind]} description="" className="py-8" /></div>;
    $[8] = compositionSegments;
    $[9] = hasRows;
    $[10] = hoveredKey;
    $[11] = isError;
    $[12] = isLoading;
    $[13] = kind;
    $[14] = legendItems;
    $[15] = onRetry;
    $[16] = rows;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== t5 || $[19] !== t6 || $[20] !== t7) {
    t8 = <NebulyPageCard title={SPEND_DISTRIBUTION_CARD_TITLE} description={SPEND_DISTRIBUTION_CARD_TOOLTIP} className={t5} contentClassName="flex flex-col gap-4 !py-4" testId="spend-distribution-card">{t6}{t7}</NebulyPageCard>;
    $[18] = t5;
    $[19] = t6;
    $[20] = t7;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  return t8;
}
function _temp(segment) {
  return {
    key: segment.key,
    name: segment.label,
    value: segment.value,
    share: segment.share,
    color: segment.color
  };
}